/** Copyright (c) 2019 T.Rowe Price. All rights reserved. */

import moment from 'moment';
import { invert } from 'lodash';

import Content from 'services/Content';

import {
  PURCHASE_TRANSACTION_TYPE,
  REDEEM_TRANSACTION_TYPE,
  EXCHANGE_TRANSACTION_TYPE
} from 'services/Accounts';

export const DEFAULT_TRANSACTIONS_DAYS = 5;
export const RAS_FEE = 5710;
export const RETIREMENT_ADVISORY_FEE_LABEL = 'RETIREMENT ADVISORY FEE';
export const SHARE_CLASS_CONVERSION_LABEL = 'SHARE CLASS CONVERSION';

export const detailToContentMap = {
  'ACCOUNT SERVICE FEE': {
    content: Content.accountServiceFeeDefinition,
    title: 'Account Service Fee Definition'
  },
  'CLOSEOUT FEE': {
    content: Content.closeoutFeeDefinition,
    title: 'Closeout Fee Definition'
  },
  'XXXX MAINTENANCE FEE': {
    content: Content.brokerageFeeDefinition,
    title: 'Brokerage Fee Definition'
  },
  'REDEMPTION FEE': {
    content: Content.redemptionFeeDefinition,
    title: 'Redemption Fee Definition'
  }
};

export const TRANSACTION_DETAILS = Object.keys(detailToContentMap);

export const CANCEL_TRANSACTION_TYPES = {
  CANCEL_EXCHANGE: EXCHANGE_TRANSACTION_TYPE,
  CANCEL_PURCHASE: PURCHASE_TRANSACTION_TYPE,
  CANCEL_REDEMPTION: REDEEM_TRANSACTION_TYPE
};

const mapType = invert(CANCEL_TRANSACTION_TYPES);

export const filterPendingTransactionsByAccount = (
  account,
  transactions,
  limit = 5
) => {
  const { accountNumber, accountChecksum } = account;
  const numberWithChecksum = `${accountNumber}-${accountChecksum}`;
  const filteredTransactions = transactions.filter((transaction) => {
    const {
      fromInformation: { accountFromInfo },
      toInformation
    } = transaction;
    let isTo = toInformation.toAccountWChecksum === numberWithChecksum;
    let isFrom = false;
    if (accountFromInfo) {
      isFrom = accountFromInfo.accountWChecksum === numberWithChecksum;
    }
    return isFrom || isTo;
  });
  return filteredTransactions
    .sort((a, b) => moment.utc(b.tradeDate).isBefore(moment.utc(a.tradeDate)))
    .slice(0, limit);
};

export const buildBusinessEvent = (
  allPendingTransactions,
  view,
  confirmationNumber,
  customer,
  customerEmail
) => {
  const { hasMaryland529Only, hasAlaska529Only, hasNational529Only } = view;

  const pendingTransaction = allPendingTransactions.find(
    (pt) => pt.confirmationNumber === confirmationNumber
  );

  if (pendingTransaction && pendingTransaction.type === 1) {
    // if roth conversion we need to map type to exchange to utilize cancel template
    pendingTransaction.type = 132;
  }

  const eventType = mapType[pendingTransaction.type];

  const clientSegment = hasMaryland529Only
    ? 'MD529'
    : hasAlaska529Only
    ? 'UA529'
    : hasNational529Only
    ? 'TRP529'
    : 'RETAIL';

  return {
    eventType: eventType,
    emailAddress: customerEmail,
    brandingProfile: clientSegment,
    entityKey: {
      businessPartyId: customer.businessPartyId,
      uberId: customer.uberId
    },
    pendingTransaction: pendingTransaction
  };
};

export const selectionIsShareClassConversion = (associatedWithShareClassConversion, selection) =>
  associatedWithShareClassConversion && selection === SHARE_CLASS_CONVERSION_LABEL;
